import React, { useState, useEffect } from 'react';
import { FaEye, FaEdit, FaTrash, FaTruck, FaSearch, FaMapMarkerAlt, FaCheckCircle, FaTimesCircle, FaSpinner, FaUser, FaEnvelope, FaPhone, FaLock, FaIdCard, FaCog, FaCopy, FaChevronDown, FaChevronUp, FaFileAlt } from 'react-icons/fa';
import { userService, getDocumentUrl } from '../../../../services/apiService';
import { getDisplayRole, getProfileDisplayEmail, getProfileDisplayName } from '../../../../utils/displayName';
import { toast } from 'react-toastify';

const isDocumentUrl = (v) => {
  if (v == null) return false;
  if (typeof v === 'string') return /^\/uploads\//.test(v) || /^uploads\//.test(v) || /^https?:\/\//.test(v);
  if (typeof v === 'object' && v !== null && typeof v.path === 'string') return true;
  return false;
};
const getDocumentValue = (v) => (v == null ? '' : typeof v === 'object' && v !== null && v.path != null ? v.path : String(v));

const DataRow = ({ label, value, icon: Icon, copyable, onCopy }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (value == null || value === '') return;
    const str = Array.isArray(value) ? value.join(', ') : String(value);
    if (str === '••••••••') return;
    navigator.clipboard?.writeText(str).then(() => {
      setCopied(true);
      onCopy?.();
      setTimeout(() => setCopied(false), 1500);
    });
  };
  const documentUrl = getDocumentValue(value);
  const isDoc = isDocumentUrl(value);
  const display = value == null ? '—' : value === true ? 'Yes' : value === false ? 'No' : Array.isArray(value) ? (value.length ? value.join(', ') : '—') : isDoc ? 'View document' : String(value);
  const showCopy = copyable && display && display !== '—' && display !== '••••••••' && !isDoc;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0 group">
      {Icon && <div className="mt-0.5 w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 shrink-0 group-hover:bg-gray-200 transition-colors"><Icon className="text-sm" /></div>}
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
        {isDoc && documentUrl ? (
          <a href={getDocumentUrl(documentUrl)} target="_blank" rel="noopener noreferrer" className="mt-0.5 text-sm font-medium text-sky-600 hover:underline break-words inline-flex items-center gap-1">
            <FaFileAlt className="text-xs shrink-0" /> View document
          </a>
        ) : (
          <p className="mt-0.5 text-sm font-medium text-gray-900 break-words">{display}</p>
        )}
      </div>
      {showCopy && (
        <button type="button" onClick={handleCopy} className="mt-0.5 p-2 rounded-lg text-gray-400 hover:text-sky-600 hover:bg-sky-50 transition-all shrink-0" title="Copy">
          <FaCopy className={`text-xs ${copied ? 'text-sky-500' : ''}`} />
        </button>
      )}
    </div>
  );
};

const Shipping = () => {
  const [view, setView] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [shippingList, setShippingList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedAccount, setExpandedAccount] = useState(true);
  const [expandedDelivery, setExpandedDelivery] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', phoneCode: '+91', password: '', status: 'Active'
  });

  useEffect(() => {
    fetchShipping();
  }, []);

  useEffect(() => {
    if (view === 'view' && selectedShipping) {
      setExpandedAccount(true);
      setExpandedDelivery(true);
    }
  }, [view, selectedShipping?.id, selectedShipping?._id]);

  const fetchShipping = async () => {
    try {
      setLoading(true);
      const response = await userService.getAllUsers();
      const users = response.data || response.users || response;
      const list = Array.isArray(users)
        ? users.filter(u => (u.role || '').toLowerCase() === 'shipping').map(user => ({
            id: user._id || user.id,
            _id: user._id || user.id,
            name: user.name || user.shippingDetails?.name || user.deliveryDetails?.name || '—',
            email: getProfileDisplayEmail(user) || '—',
            phone: user.phone || '',
            phoneCode: user.phoneCode || '+91',
            role: user.role,
            kycStatus: user.kycStatus || user.shippingDetails?.kycStatus || 'Pending',
            status: user.isActive !== undefined ? (user.isActive ? 'Active' : 'Inactive') : (user.status || 'Active'),
            shippingDetails: user.shippingDetails || user.deliveryDetails,
            createdAt: user.createdAt
          }))
        : [];
      setShippingList(list);
    } catch (error) {
      console.error('Failed to fetch shipping:', error);
      toast.error('Failed to load shipping: ' + (error.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const filteredShipping = shippingList.filter(s => {
    const matchesSearch = !searchTerm ||
      (s.name && s.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (s.email && s.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (s.phone && s.phone.includes(searchTerm));
    const statusNorm = (s.status || 'Active').toString().trim().toLowerCase();
    const filterNorm = statusFilter.toString().trim().toLowerCase();
    const matchesStatus = statusFilter === 'all' || filterNorm === statusNorm;
    return matchesSearch && matchesStatus;
  });

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    toast.info('Filters cleared');
  };

  const handleKycStatusChange = async (item, newKycStatus) => {
    const id = item.id || item._id;
    try {
      await userService.updateUser(id, { kycStatus: newKycStatus });
      setShippingList(prev => prev.map(s => (s.id === id || s._id === id) ? { ...s, kycStatus: newKycStatus } : s));
      toast.success('KYC status updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update KYC status');
    }
  };

  const handleAction = (type, item) => {
    setSelectedShipping(item);
    if (type === 'view') setView('view');
    else if (type === 'edit') {
      setFormData({
        name: item.name || '',
        email: item.email || '',
        phone: item.phone || '',
        phoneCode: item.phoneCode || '+91',
        password: '',
        status: item.status || 'Active'
      });
      setView('edit');
    } else if (type === 'delete') {
      if (window.confirm(`Remove ${item.name || 'this delivery partner'}?`)) {
        handleDelete(item.id || item._id);
      }
    }
  };

  const handleDelete = async (userId) => {
    try {
      setLoading(true);
      await userService.deleteUser(userId);
      toast.success('Delivery partner removed');
      setView('list');
      setSelectedShipping(null);
      fetchShipping();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to remove: ' + (error.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedShipping) return;
    try {
      setSaving(true);
      const userId = selectedShipping.id || selectedShipping._id;
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        phoneCode: formData.phoneCode || '+91',
        status: formData.status
      };
      if (formData.password && formData.password.trim()) payload.password = formData.password.trim();
      await userService.updateUser(userId, payload);
      toast.success('Delivery partner updated');
      setView('list');
      setFormData({ name: '', email: '', phone: '', phoneCode: '+91', password: '', status: 'Active' });
      setSelectedShipping(null);
      fetchShipping();
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save: ' + (error.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const getProfileImage = (s) => (s?.avatar || s?.profilePhoto || s?.image || s?.shippingDetails?.photo || '');

  if (view === 'edit') {
    return (
      <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-xl font-bold text-gray-900 tracking-tight">Edit Delivery Partner</h1>
          <button type="button" onClick={() => setView('list')} className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors">← Back</button>
        </div>
        <form onSubmit={handleSave} className="space-y-6">
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/80">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <FaTruck className="text-gray-500" /> Account details
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Name *</label>
                  <input required type="text" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Delivery partner name" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email *</label>
                  <input required type="email" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="email@example.com" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Phone</label>
                  <input type="text" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="9876543210" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Phone code</label>
                  <input type="text" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition" value={formData.phoneCode} onChange={e => setFormData({ ...formData, phoneCode: e.target.value })} placeholder="+91" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Status</label>
                  <select className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 bg-white" value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Suspended">Suspended</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Password (leave empty to keep existing)</label>
                  <input type="password" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} placeholder="Leave empty to keep existing" />
                </div>
              </div>
            </div>
          </section>
          <div className="flex flex-col sm:flex-row gap-3">
            <button type="submit" disabled={saving} className="flex-1 py-3 px-4 bg-sky-600 text-white text-sm font-semibold rounded-lg hover:bg-sky-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {saving && <FaSpinner className="animate-spin" />}
              Update
            </button>
            <button type="button" onClick={() => setView('list')} className="flex-1 py-3 px-4 bg-white border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50">Cancel</button>
          </div>
        </form>
      </div>
    );
  }

  if (view === 'view' && selectedShipping) {
    const displayName = selectedShipping.shippingDetails?.name || selectedShipping.deliveryDetails?.name || '';
    const displayEmail = getProfileDisplayEmail(selectedShipping);
    const fullPhone = (selectedShipping.phoneCode || '') + (selectedShipping.phone || '');
    const handleContact = () => {
      if (displayEmail) window.location.href = `mailto:${displayEmail}`;
      else if (fullPhone) window.location.href = `tel:${fullPhone.replace(/\s/g, '')}`;
      else toast.info('No contact details');
    };
    const sd = selectedShipping.shippingDetails || {};

    return (
      <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-xl font-bold text-gray-900 tracking-tight">Delivery Partner Profile</h1>
          <button onClick={() => setView('list')} className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors">← Back</button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 sm:p-8 flex flex-col sm:flex-row gap-6">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl flex items-center justify-center text-white shrink-0 overflow-hidden bg-gradient-to-br from-sky-500 to-sky-600 ring-2 ring-white ring-offset-2 shadow-lg">
              {getProfileImage(selectedShipping) ? (
                <img loading="lazy" src={getProfileImage(selectedShipping)} alt={displayName} className="w-full h-full object-cover" />
              ) : (
                <FaTruck className="w-10 h-10 sm:w-12 sm:h-12 text-sky-200" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold text-gray-900 truncate">{displayName}</h2>
              {displayEmail ? <a href={`mailto:${displayEmail}`} className="mt-1 block text-sm text-slate-600 hover:underline truncate">{displayEmail}</a> : null}
              {(selectedShipping.phone || selectedShipping.phoneCode) && (
                <a href={`tel:${fullPhone.replace(/\s/g, '')}`} className="mt-0.5 inline-block text-sm text-slate-600 hover:underline">
                  {(selectedShipping.phoneCode || '') + ' ' + (selectedShipping.phone || '')}
                </a>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700">{getDisplayRole(selectedShipping) || 'Delivery'}</span>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${(selectedShipping.status || '').toLowerCase() === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                  {selectedShipping.status ?? '—'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <button type="button" onClick={() => setExpandedAccount(e => !e)} className="w-full px-6 py-4 border-b border-gray-100 bg-gray-50/80 flex items-center justify-between gap-2 text-left hover:bg-gray-100/80 transition-colors">
            <div className="flex items-center gap-2">
              <FaIdCard className="text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900">Account Information</h3>
            </div>
            <span className="text-gray-400">{expandedAccount ? <FaChevronUp /> : <FaChevronDown />}</span>
          </button>
          {expandedAccount && (
            <div className="px-6 py-4 divide-y divide-gray-100">
              <DataRow label="Name" value={selectedShipping.shippingDetails?.name || selectedShipping.deliveryDetails?.name || ''} icon={FaUser} copyable onCopy={() => toast.success('Name copied')} />
              <DataRow label="Email" value={displayEmail || '—'} icon={FaEnvelope} copyable onCopy={() => toast.success('Email copied')} />
              <DataRow label="Password" value="••••••••" icon={FaLock} />
              <DataRow label="Photo" value={getProfileImage(selectedShipping) || null} icon={FaFileAlt} />
              <DataRow label="Phone" value={selectedShipping.phone || '—'} icon={FaPhone} copyable onCopy={() => toast.success('Phone copied')} />
              <DataRow label="Phone code" value={selectedShipping.phoneCode || (selectedShipping.phone ? '+91' : '') || '—'} icon={FaPhone} copyable onCopy={() => toast.success('Phone code copied')} />
              <DataRow label="Role" value={getDisplayRole(selectedShipping) || selectedShipping.role || '—'} icon={FaCog} copyable onCopy={() => toast.success('Role copied')} />
            </div>
          )}
        </section>

        {(sd.vehicleType || sd.area || sd.deliveryAreas) && (
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <button type="button" onClick={() => setExpandedDelivery(e => !e)} className="w-full px-6 py-4 border-b border-gray-100 bg-gray-50/80 flex items-center justify-between gap-2 text-left hover:bg-gray-100/80 transition-colors">
              <div className="flex items-center gap-2">
                <FaTruck className="text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-900">Delivery Information</h3>
              </div>
              <span className="text-gray-400">{expandedDelivery ? <FaChevronUp /> : <FaChevronDown />}</span>
            </button>
            {expandedDelivery && (
              <div className="px-6 py-4 divide-y divide-gray-100">
                {sd.vehicleType && <DataRow label="Vehicle type" value={sd.vehicleType} icon={FaTruck} copyable onCopy={() => toast.success('Copied')} />}
                {sd.area && <DataRow label="Area" value={sd.area} icon={FaMapMarkerAlt} copyable onCopy={() => toast.success('Copied')} />}
                {sd.deliveryAreas && <DataRow label="Delivery areas" value={Array.isArray(sd.deliveryAreas) ? sd.deliveryAreas.join(', ') : sd.deliveryAreas} icon={FaMapMarkerAlt} copyable onCopy={() => toast.success('Copied')} />}
              </div>
            )}
          </section>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={() => handleAction('edit', selectedShipping)} className="flex-1 py-3 px-4 bg-sky-600 text-white text-sm font-semibold rounded-lg hover:bg-sky-700">
            Edit
          </button>
          <button onClick={handleContact} className="flex-1 py-3 px-4 bg-white border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50">
            Contact
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 tracking-tight">Shipping / Delivery Partners</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage delivery partners</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative lg:col-span-2">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Search by name, email or phone..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 bg-white"
          >
            <option value="all">All status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Suspended">Suspended</option>
          </select>
          <button type="button" onClick={handleClearFilters} disabled={!searchTerm && statusFilter === 'all'} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50">
            <FaTimesCircle /> Clear
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-3">Showing {filteredShipping.length} of {shippingList.length} partner{shippingList.length !== 1 ? 's' : ''}</p>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12 bg-white rounded-xl border border-gray-200">
          <FaSpinner className="animate-spin text-sky-600 text-3xl" />
        </div>
      )}

      {!loading && filteredShipping.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <FaTruck className="mx-auto text-gray-300 text-4xl mb-3" />
          <p className="text-gray-600 font-medium">No delivery partners found</p>
          <p className="text-sm text-gray-500 mt-1">{shippingList.length === 0 ? 'No shipping partners in the system yet.' : 'Try adjusting search or filters.'}</p>
          {(searchTerm || statusFilter !== 'all') && <button type="button" onClick={handleClearFilters} className="mt-4 text-sm font-medium text-sky-600 hover:text-sky-700">Clear filters</button>}
        </div>
      )}

      {!loading && filteredShipping.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Partner</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">KYC Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredShipping.map((item) => (
                  <tr key={item.id || item._id} className="hover:bg-gray-50/50 transition">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-sky-600 rounded-xl flex items-center justify-center text-white shadow-sm shrink-0">
                          <FaTruck className="text-sm" />
                        </div>
                        <div className="min-w-0">
                          <span className="text-sm font-semibold text-gray-900 block truncate">
                            {item.shippingDetails?.name || item.deliveryDetails?.name || '—'}
                          </span>
                          <span className="text-xs text-gray-500">{item.phone ? (item.phoneCode || '') + ' ' + item.phone : '—'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4"><span className="text-sm text-gray-700">{getProfileDisplayEmail(item) || ''}</span></td>
                    <td className="px-6 py-4"><span className="text-sm text-gray-700">{item.phone || ''}</span></td>
                    <td className="px-6 py-4">
                      <select
                        value={item.kycStatus || 'Pending'}
                        onChange={(e) => handleKycStatusChange(item, e.target.value)}
                        className={`text-xs font-medium rounded-lg px-2.5 py-1.5 border cursor-pointer ${(item.kycStatus || 'Pending') === 'Verified' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : (item.kycStatus || 'Pending') === 'Rejected' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Verified">Verified</option>
                        <option value="Rejected">Rejected</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${(item.status || 'Active').toString().toLowerCase() === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                        {item.status || 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleAction('view', item)} className="p-2 text-sky-600 hover:bg-sky-50 rounded-lg transition" title="View"><FaEye /></button>
                        <button onClick={() => handleAction('edit', item)} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition" title="Edit"><FaEdit /></button>
                        <button onClick={() => handleAction('delete', item)} disabled={loading} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50" title="Delete"><FaTrash /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shipping;
